import { ClientWithAuth, ClientWithoutAuth } from 'client/services/service.js';
import { getLocale } from 'i18n/localeUtils';

const serviceUrlSuffix = '/cms';
const extractMarkdown = (response) => response?.data?.markdown;
const extractMessages = (response) => {
    const messages = response?.messages || [];
    return messages;
};
const extractPlatformNotices = (response) => ({
    notices: response?.notices || [],
    lastUpdated: response?.lastUpdated || null
});

const MAX_CACHE_AGE_MARKDOWN = 5 * 60 * 1000;
const MAX_CACHE_ENTRIES_MARKDOWN = 4;
const MAX_CACHE_AGE_JSON = 1 * 60 * 1000;
const MAX_CACHE_ENTRIES_JSON = 4;

const _cacheStore = {};
const cacher = ({ name, maxAgeMS, maxEntries }) => {
    return {
        get: (key) => {
            const entry = (_cacheStore[name] || {})[key];
            if (!entry || entry.created < Date.now() - maxAgeMS) {
                return null;
            }
            return entry.value;
        },
        set: (key, value) => {
            _cacheStore[name] = _cacheStore[name] || {};
            _cacheStore[name][key] = {
                value,
                created: Date.now()
            };
            const keys = Object.keys(_cacheStore[name]);
            if (keys.length > maxEntries) {
                delete _cacheStore[name][keys[0]];
            }
        }
    };
};

const getMarkdownFor = (pageUrl, secure, isLanguageAgnostic) => {
    return async () => {
        const locale = getLocale();

        const cache = cacher({
            name: 'cms',
            maxAgeMS: MAX_CACHE_AGE_MARKDOWN,
            maxEntries: MAX_CACHE_ENTRIES_MARKDOWN
        });

        const key = `${pageUrl}-${!!secure}-${locale}`;
        const cacheHit = cache.get(key);
        if (cacheHit) {
            return cacheHit;
        }

        // Release notes and what working on are language agnostic, so we don't need to append the locale
        const languageVariant = isLanguageAgnostic ? `${pageUrl}` : `${pageUrl}-${locale}`;

        // Haven't requested this content yet, so fetch it
        // Occasionally, the first request hasn't returned when we do the memo check,
        // in which case we will still have multiple requests for the same content, but it's still an improvement.
        let response;
        if (secure) {
            response = await ClientWithAuth.get(`${serviceUrlSuffix}/secure-content/${languageVariant}`);
        } else {
            response = await ClientWithoutAuth.get(`${serviceUrlSuffix}/content/${languageVariant}`);
        }
        const content = {
            markdown: extractMarkdown(response)
        };

        cache.set(key, content);
        return content;
    };
};

// Get JSON content from CMS for system messages
const getJsonFor = (pageUrl, processor = (data) => data) => {
    return async () => {
        const cache = cacher({
            name: 'json',
            maxAgeMS: MAX_CACHE_AGE_JSON,
            maxEntries: MAX_CACHE_ENTRIES_JSON
        });

        const key = pageUrl;
        const cacheHit = cache.get(key);
        if (cacheHit) {
            return cacheHit;
        }

        const response = await ClientWithAuth.get(`${serviceUrlSuffix}/secure-alerts/${pageUrl}`);
        const processed = processor(response.data);
        cache.set(key, processed);
        return processed;
    };
};

const getMedia = async (pageUrl, secure) => {
    let response;
    if (!pageUrl.startsWith('/')) {
        return;
    }
    if (secure) {
        response = await ClientWithAuth.get(`${serviceUrlSuffix}/secure-media${pageUrl}`, { responseType: 'blob' });
    } else {
        response = await ClientWithoutAuth.get(`${serviceUrlSuffix}/media${pageUrl}`, { responseType: 'blob' });
    }

    return URL.createObjectURL(response.data);
};

export default {
    getPrivacyPolicy: getMarkdownFor('privacy-policy'),
    getCookiePolicy: getMarkdownFor('cookies-policy'),
    getContactSignedOut: getMarkdownFor('contact-signed-out'),
    getContact: getMarkdownFor('contact', true),
    getTsAndCs: getMarkdownFor('terms-and-conditions', true),
    getHelpSignedOut: getMarkdownFor('signed-out-help-page'),
    getHelp: getMarkdownFor('help-page-index', true),
    getHelpContent: async (key) => getMarkdownFor(key, true)(),
    getHomePageAbout: getMarkdownFor('home-page-about'),
    getWorkspaceNotice: getMarkdownFor('workspace-notice', true),
    getAcceptableUsage: getMarkdownFor('acceptable-usage', true),
    getHSG47: getMarkdownFor('HSG47', true),
    getNews: getMarkdownFor('news', true),
    getNewsContent: async (key) => getMarkdownFor(key, true)(),
    getNoOrganisations: getMarkdownFor('no-organisations'),
    getObservationsDisclaimer: getMarkdownFor('observations-disclaimer', true),
    getReleaseNotes: getMarkdownFor('release-notes', true, true),
    getWorkingOn: getMarkdownFor('what-working-on', true, true),
    getFeedbackFormConfirmation: getMarkdownFor('feedback-confirmation', true),
    getFeedbackDescription: getMarkdownFor('feedback-page-description', true),
    getMedia: getMedia,
    getDisplayMessages: getJsonFor('displayMessage', extractMessages),
    getPlatformNotices: getJsonFor('platformNotices', extractPlatformNotices),
    _clearCache: () => { // for testing
        Object.keys(_cacheStore).forEach((key) => {
            delete _cacheStore[key];
        });
    }
};
